<script setup>
import VideoCallMenuButton from "@/components/Common/VideoCallMenuButton.vue";
import { ref, onMounted, onUnmounted } from "vue";
import i18n from "@/i18n";

import { EventManager, Events } from "@events";
import axios from "axios";
import { useRecordingStartTime } from "../composables/useRecordingStartTime";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  sectionType: {
    type: String,
    required: false,
  },
  modelValue: {
    type: Object,
    required: false,
  },
});

const { recordingStartTime, setupListener: setupRecordingListener } = useRecordingStartTime(props.rtcId);

let eventEmitter = null;
let actionEmitter = null;

const isBtnDisabled = ref(false);
const snippetActive = ref(false);
const activeSnippetId = ref(null);

const actions = ref([
  {
    name: i18n.global.t("StartFrom30sAgo"),
    icon: "mdi-rewind-30",
    trigger: () => startSnippet(30),
  },
  {
    name: i18n.global.t("StartFrom10sAgo"),
    icon: "mdi-rewind-10",
    trigger: () => startSnippet(10),
  },
  {
    name: i18n.global.t("StartNow"),
    icon: "mdi-record-circle-outline",
    trigger: () => startSnippet(0),
  },
]);

const cleanupFns = [];

onMounted(async () => {
  eventEmitter = EventManager.getEventEmitter(props.rtcId);
  actionEmitter = EventManager.getActionEmitter(props.rtcId);

  const cleanupRecording = await setupRecordingListener();
  cleanupFns.push(cleanupRecording);

  // Listen for snippet stopped from SnippetActiveBar
  const onSnippetStopped = () => {
    snippetActive.value = false;
    activeSnippetId.value = null;
    isBtnDisabled.value = false;
  };

  actionEmitter.on("snippet-stopped", onSnippetStopped);
  cleanupFns.push(() => actionEmitter.off("snippet-stopped", onSnippetStopped));
});

onUnmounted(() => {
  cleanupFns.forEach((fn) => fn());
});

async function startSnippet(offsetSeconds) {
  if (snippetActive.value) return;

  isBtnDisabled.value = true;

  const elapsedSeconds = recordingStartTime.value
    ? Math.floor((Date.now() - recordingStartTime.value) / 1000)
    : 0;
  const startSecond = Math.max(0, elapsedSeconds - offsetSeconds);

  try {
    const response = await axios.post(
      `/api/Snippet/StartSnippet/${props.rtcId}`,
      {
        startSecond,
      }
    );

    if (response.status === 200 && response.data?.success) {
      snippetActive.value = true;
      activeSnippetId.value = response.data.data?.id;

      // Notify SnippetActiveBar via action event
      actionEmitter.emit("snippet-started", {
        snippetId: activeSnippetId.value,
        recordingStartTime: recordingStartTime.value,
        offsetSeconds,
      });

      eventEmitter.emit(Events.CallEvent, {
        name: "SnippetStarted",
        // Store only the language-neutral second marker; admin renders the event text in the viewer's language.
        data: `${startSecond}s`,
      });

      showSnackbar(i18n.global.t("SnippetStarted"));
    } else {
      isBtnDisabled.value = false;
      showSnackbar(i18n.global.t("SnippetFailed"), "red");
    }
  } catch (error) {
    console.error(error);
    isBtnDisabled.value = false;
    showSnackbar(i18n.global.t("SnippetFailed"), "red");
  }
}

function showSnackbar(text, color = "green") {
  if (actionEmitter) {
    actionEmitter.emit("snippet-show-snackbar", { text, color });
  }
}
</script>

<template>
  <VideoCallMenuButton
    :icon="'mdi-content-cut'"
    :actions="actions"
    :disabled="isBtnDisabled || snippetActive"
  />
</template>

<style scoped>
</style>
